import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useAuth } from "@/contexts/auth-context";
import { cn } from "@/lib/utils";
import { ChevronRight, Menu } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { FiPhone } from "react-icons/fi";
import { Link, useLocation } from "react-router-dom";
import { MobileSubmenu } from "./MobileSubmenu";
import { SOBRE_DATA, SOLUCOES_DATA } from "./menu-data";

export function HeaderClean() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileView, setMobileView] = useState<"root" | "sobre" | "solucoes">("root");
  const { user, signOut } = useAuth();
  const { pathname } = useLocation();

  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 0);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isQuotePage =
    pathname.includes("/fazer-cotacao") || pathname.includes("/cotacao/");

  return (
    <header
      ref={headerRef}
      className={cn(
        "fixed inset-x-0 top-0 z-50",
        "backdrop-blur-md transition-colors",
        scrolled ? "bg-n-green/95" : "bg-n-green"
      )}
    >
      <div className="container flex h-[56px] items-center justify-between md:h-[72px]">
        <Link to="/" className="flex items-center gap-3">
          <img
            src="/images/logo_xl.png"
            alt="Nacional GO"
            width={120}
            height={28}
            className="h-4 md:h-7 w-auto"
          />
          <span className="sr-only">Nacional GO</span>
        </Link>

        <div className="flex items-center md:gap-3 gap-1">
          {!isQuotePage ? (
            <Button variant="outline" size="small">
              <Link to="/fazer-cotacao">Fazer Cotação</Link>
            </Button>
          ) : (
            <Button
              variant="outline"
              size="small"
              className="text-xs md:text-base"
            >
              <FiPhone />
              <a href="tel:08008780708">0800 878 0708</a>
            </Button>
          )}

          <Sheet
            open={mobileOpen}
            onOpenChange={(o) => {
              setMobileOpen(o);
              if (!o) setMobileView("root");
            }}
          >
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="text-white lg:hidden"
              >
                <Menu className="h-5 w-5" />
                <span className="sr-only">Abrir menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-full max-w-sm p-0">
              {mobileView === "root" ? (
                <div className="flex h-full flex-col">
                  <SheetHeader className="px-4 pb-2 pt-4">
                    <SheetTitle className="text-left text-n-text-green">
                      Nacional GO
                    </SheetTitle>
                  </SheetHeader>
                  <div className="space-y-1 p-2">
                    <button
                      className="flex w-full items-center justify-between rounded-md px-3 py-3 text-left text-base hover:bg-n-green-h-light"
                      onClick={() => setMobileView("sobre")}
                    >
                      Sobre <ChevronRight className="h-4 w-4" />
                    </button>
                    <button
                      className="flex w-full items-center justify-between rounded-md px-3 py-3 text-left text-base hover:bg-n-green-h-light"
                      onClick={() => setMobileView("solucoes")}
                    >
                      Soluções <ChevronRight className="h-4 w-4" />
                    </button>

                    {!user ? (
                      <Link
                        to="/login"
                        className="block rounded-md px-3 py-3 text-base hover:bg-n-green-h-light"
                        onClick={() => setMobileOpen(false)}
                      >
                        Entrar
                      </Link>
                    ) : (
                      <>
                        <Link
                          to="/cliente"
                          className="block rounded-md px-3 py-3 text-base hover:bg-n-green-h-light"
                          onClick={() => setMobileOpen(false)}
                        >
                          Área do Cliente
                        </Link>
                        <button
                          className="w-full rounded-md px-3 py-3 text-left text-base text-red-700 hover:bg-n-green-h-light"
                          onClick={() => {
                            signOut();
                            setMobileOpen(false);
                          }}
                        >
                          Sair
                        </button>
                      </>
                    )}

                    <div className="p-2">
                      <Link
                        to="/fazer-cotacao"
                        onClick={() => setMobileOpen(false)}
                      >
                        <Button className="w-full bg-n-green-action text-n-text-green hover:bg-n-green-4">
                          Fazer Orçamento
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              ) : (
                <MobileSubmenu
                  title={mobileView === "sobre" ? "Sobre" : "Soluções"}
                  data={mobileView === "sobre" ? SOBRE_DATA : SOLUCOES_DATA}
                  onBack={() => setMobileView("root")}
                  onNavigate={() => setMobileOpen(false)}
                />
              )}
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
